<template>
  <div class="loading-page">
    <div class="loading-page-spin-box">
      <RexSpinV3 size="large" />
      <div>{{ $t('pleaseWait') }}</div>
      <div class="loading-page-load-text">
        {{ $t('loading') }}
      </div>
    </div>
  </div>
</template>
<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import { TEMPLATE_LOADING_PAGE } from '@/utils/constants';
import { AuthService } from '@/services/api/auth.service';
import { notificationError } from '@/utils/notification';
import RexSpinV3 from '@/rex-components/RexSpinV3.vue';
import { defaultValueMutations } from '@/utils/constants';
import { setRedirectionServiceWithPermissions } from '@/router/utils';
import { usePermissions } from '@/hooks/usePermissions';
import { parseServiceObjFromApplication } from '@/utils/helper-function';
import { permissionTypes } from '@/router/constant';

export default {
  name: 'LoadingPage',
  components: { RexSpinV3 },
  computed: {
    ...mapGetters('globalState/user', ['getTeamId']),
  },
  data() {
    return {
      isError: false,
    };
  },
  methods: {
    ...mapMutations('globalState/app', ['setTemplate', 'setPermissions']),
    ...mapMutations('globalState/user', {
      setIsAuthenticatedMutation: 'setIsAuthenticated',
      setTeamIdMutation: 'setTeamId',
      setTeamNameMutation: 'setTeamName',
    }),
    ...mapActions('globalState/user', ['getTokenForOapAccess', 'getProfile']),
    async updateAuth() {
      try {
        const { data } = await AuthService.setCookieForTeam(this.getTeamId);
        if (data) {
          await this.resetTenantData();
          const redirectPath = this.$route.query?.redirectPath;
          const updatedQuery = { ...this.$route.query };
          if (updatedQuery.retainRedirectionState === 'true') {
            console.log('route', this.$route);
            console.log('retainRedirectionState', this.$route.query);
            await this.setRedirectionState(this.$route.query);
          }
          delete updatedQuery.updateAuth;
          delete updatedQuery.redirectPath;
          delete updatedQuery.retainRedirectionState;
          this.$router.push({
            path: redirectPath || '/home',
            query: updatedQuery,
          });
        } else {
          this.$router.push({
            name: 'home',
            query: { updateAuth: 'false', updateTeamData: 'false' },
          });
          notificationError(this.$t('changeTenantError'));
        }
      } catch (error) {
        console.log('error', error);
        this.$router.push({
          name: 'home',
          query: { updateAuth: 'false', updateTeamData: 'false' },
        });
        notificationError(this.$t('changeTenantError'));
      }
    },
    async useCodeAndStateToLogin(code, state) {
      try {
        const { data } = await AuthService.getAccessTokenFromCodeAndState(
          code,
          state,
        );
        if (data.success) {
          this.setIsAuthenticatedMutation(true);
          this.setTeamIdMutation(data.teamsList?.teams?.[0]?.teamId);
          this.setTeamNameMutation(data.teamsList?.teams?.[0]?.teamName);
          this.$router.push('/home');
        } else {
          this.setIsAuthenticatedMutation(false);
          notificationError(this.$t('loginError'));
          this.$router.push('/login');
        }
      } catch (error) {
        console.log('error', error);
        if (error?.message) {
          notificationError(error.message);
        } else {
          notificationError(this.$t('loginError'));
        }
        this.setIsAuthenticatedMutation(false);
        this.$router.push('/login');
      }
    },
    resetToDefaultValues() {
      defaultValueMutations.forEach((mutation) => {
        this.$store.commit(mutation, {});
      });
    },
    async resetTenantData() {
      this.setPermissions();
      this.resetToDefaultValues();
      await this.getTokenForOapAccess(this.getTeamId);
      await this.getProfile();
    },
    async setRedirectionState(query) {
      const serviceId = query?.id ?? '';
      const category = query.cat ?? '';
      const to =
        query?.redirectPath === '/alertsV2/generatedV2'
          ? 'generatedV2'
          : query?.redirectPath;
      const service = parseServiceObjFromApplication({ key: serviceId });
      const { fetchServicePermissions } = usePermissions();
      let serviceAccessList = [];
      if (to.name == 'generatedV2') {
        serviceAccessList = [
          {
            resource: serviceId,
            resourceGroup: service.type,
            permission: permissionTypes.ALERT_READ,
          },
          {
            resource: serviceId,
            resourceGroup: service.type,
            permission: permissionTypes.ALERT_WRITE,
          },
        ];
      } else {
        serviceAccessList = [
          {
            resource: serviceId,
            resourceGroup: service.type,
            permission: permissionTypes.READ,
          },
          {
            resource: serviceId,
            resourceGroup: service.type,
            permission: permissionTypes.WRITE,
          },
        ];
      }
      const permissionList = await fetchServicePermissions(serviceAccessList);
      setRedirectionServiceWithPermissions(
        to,
        serviceId,
        service,
        permissionList,
        category,
      );
    },
  },
  mounted() {
    this.setTemplate(TEMPLATE_LOADING_PAGE);
  },
  watch: {
    '$route.query.updateAuth': {
      immediate: true,
      async handler(newValue) {
        if (newValue === 'true') {
          await this.updateAuth();
        }
      },
    },
    '$route.query.code': {
      immediate: true,
      handler(newCode) {
        if (newCode) {
          this.useCodeAndStateToLogin(newCode, this.$route.query?.state);
        }
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.loading-page {
  @include flex-center;
  width: 100%;
  height: 100vh;
}
.loading-page-spin-box {
  @include flex-center;
  flex-direction: column;
}
.loading-page-load-text {
  font-size: 1.75rem;
  font-weight: 600;
}
</style>
